import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import TranslateIcon from '@mui/icons-material/Translate';
import TuneIcon from '@mui/icons-material/Tune';
import { func } from 'prop-types';
import React, { useCallback, useState } from 'react';
import { I18n } from 'utils/i18n';
import { generateMessageStream } from 'services/api/AiWritingAssistant';

const gradientC0 = '#6269e5';
const gradientC1 = '#9d62e5';

const TONE_OPTIONS = ['Professional', 'Friendly', 'Formal', 'Casual'];
const LANGUAGE_OPTIONS = ['Spanish', 'English', 'Catalan'];

const AiAssistantBar = ({ onClose, onGeneratedMessage }) => {
  const theme = useTheme();
  // Using lighter colors for the header background (primary[95] to primary[90])
  const headerGradient = `linear-gradient(90deg, ${theme.palette.primary[95] || '#f1efff'}, ${
    theme.palette.primary[90] || '#e0e0ff'
  })`;
  const headerColor = theme.palette.primary.main;

  const [keyPoints, setKeyPoints] = useState('');
  const [tone, setTone] = useState(TONE_OPTIONS[0]);
  const [language, setLanguage] = useState(LANGUAGE_OPTIONS[0]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = useCallback(async () => {
    if (!keyPoints.trim()) return;
    setError(null);
    setStreaming(true);
    try {
      await generateMessageStream(
        { keyPoints: keyPoints.trim(), tone, language },
        {
          onChunk: accumulated => {
            onGeneratedMessage(accumulated);
          },
        },
      );
    } catch (err) {
      const message =
        err?.message && err.message.startsWith('AiWritingAssistant.errors.')
          ? I18n.t(err.message)
          : err?.message || I18n.t('AiWritingAssistant.errors.requestFailed');
      setError(message);
    } finally {
      setStreaming(false);
    }
  }, [keyPoints, tone, language, onGeneratedMessage]);

  const handleDiscard = useCallback(() => {
    setKeyPoints('');
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Box
      sx={{
        marginBottom: 0,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          background: headerGradient,
          color: headerColor,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 20, color: headerColor }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, letterSpacing: '0.02em', color: headerColor }}
          >
            {I18n.t('AiWritingAssistant.barTitle').toUpperCase()}
          </Typography>
        </Box>
        <IconButton
          aria-label={I18n.t('AiWritingAssistant.close')}
          onClick={onClose}
          size="small"
          disabled={streaming}
          sx={{ color: headerColor }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 2, pt: 2, pb: 2 }}>
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
          }}
        >
          {I18n.t('AiWritingAssistant.keyPointsForDraft').toUpperCase()}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          placeholder={I18n.t('AiWritingAssistant.keyPointsPlaceholder')}
          value={keyPoints}
          onChange={e => setKeyPoints(e.target.value)}
          disabled={streaming}
          variant="outlined"
          size="small"
          sx={{ mb: 1, p: 0 }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={tone}
              onChange={e => setTone(e.target.value)}
              disabled={streaming}
              displayEmpty
              renderValue={selected => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TuneIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {I18n.t(`AiWritingAssistant.tones.${selected}`)}
                  </Typography>
                </Box>
              )}
              sx={{
                '& .MuiSelect-select': {
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {TONE_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {I18n.t(`AiWritingAssistant.tones.${opt}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={language}
              onChange={e => setLanguage(e.target.value)}
              disabled={streaming}
              displayEmpty
              renderValue={selected => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TranslateIcon fontSize="small" color="action" />
                  <Typography variant="body2">{selected}</Typography>
                </Box>
              )}
              sx={{
                '& .MuiSelect-select': {
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {LANGUAGE_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Button
            variant="text"
            size="small"
            color="secondary"
            onClick={handleDiscard}
            disabled={streaming}
          >
            {I18n.t('AiWritingAssistant.discard')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={!keyPoints.trim() || streaming}
            sx={{
              background: `linear-gradient(90deg, ${
                theme.palette.colorChartSeries?.[0] ?? gradientC0
              }, ${theme.palette.colorChartSeries?.[1] ?? gradientC1})`,
              color: theme.palette.text.contrast,
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              '&:hover': { filter: 'brightness(0.92)' },
              '&.Mui-disabled': {
                background: `linear-gradient(90deg, ${
                  theme.palette.colorChartSeries?.[0] ?? gradientC0
                }, ${theme.palette.colorChartSeries?.[1] ?? gradientC1})`,
                color: theme.palette.text.contrast,
                opacity: 0.5,
              },
            }}
          >
            {streaming
              ? I18n.t('AiWritingAssistant.generating')
              : I18n.t('AiWritingAssistant.generateDraft')}
          </Button>
        </Box>
        {error && (
          <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1.5 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

AiAssistantBar.propTypes = {
  onClose: func.isRequired,
  onGeneratedMessage: func.isRequired,
};

export default AiAssistantBar;
